import React, { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';

import { trackViewedProduct } from 'utils/klaviyo';
import { useOnCheckoutClick } from 'utils/checkout-link';
import useLocalCurrency from 'utils/useLocalCurrency';
import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';
import { convertPrice, getPercentSaved } from 'utils/price';
import * as rudder from 'utils/rudderstack';
import Image from 'next/image';
import formatCurrency from 'utils/format-currency';

import MultiVariantBundleItemDesktop from './components/BundleItemDesktop';
import MultiVariantBundleItemMobile from './components/BundleItemMobile';

import type {
  ComponentsMultiVariantBundleStyle,
  ComponentsMultiVariantBundleOfferContainer,
  ComponentsMultiVariantBundleOfferContainerSalesOffer as MvbSalesOffer,
  OfferItems,
} from 'queries/graphql';
import { SalesFunnelData, useFunnelData } from 'utils/funnel-data-context';

import styles from './styles.module.scss';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

const MediaQuery = dynamic(() => import('react-responsive'), {
  ssr: false,
});

// Type for selected variants
type SelectedVariantData = {
  variantId: string;
  selectedOptions: Record<string, string>;
};

// Type for parsed product options
type ProductOption = {
  key: string;
  label: string;
  values: string[];
};

type EnhancedOfferItem = OfferItems & {
  productVariant?: {
    productOptions?: string | Record<string, any>;
    product?: {
      productId: string;
      featuredImage?: {
        src: string;
      };
    };
  };
};

export default function MultiVariantBundleItem({
  heading,
  ctaText,
  salesOffers: offers,
  style,
}: ComponentsMultiVariantBundleOfferContainer & {
  style: ComponentsMultiVariantBundleStyle;
}) {
  const {
    backgroundColor,
    borderColor,
    textColor,
    compareAtColor,
    headingColor,
    headingBackgroundColor,
    buttonTextColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonHoverTextColor,
    buttonBorderColor,
    buttonHoverBorderColor,
    headingFont,
    textFont,
    starColor,
    starEmptyColor,
    savingsBadgeTextColor,
    savingsBadgeBackgroundColor,
  } = style || {};

  const { symbol: currencySymbol, rate: localFxRate } = useLocalCurrency();

  const [shouldShowActivityIndicator, setShouldShowActivityIndicator] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // TODO: add payment method and should track options to useOnCheckoutClick
  const getCheckoutClickHandler = useOnCheckoutClick({
    setShouldShowActivityIndicator,
    shouldTrackSelectItem: true,
  });

  const [selectedVariant, setSelectedVariant] = useState<MvbSalesOffer>(offers[0]);

  const [selectedVariants, setSelectedVariants] = useState<Record<string, SelectedVariantData>>({});
  const [showVariantSelector, setShowVariantSelector] = useState(false);
  const [availableOptions, setAvailableOptions] = useState<ProductOption[]>([]);

  const variantOffers = selectedVariant?.salesPageOffer;
  const selectedOffer = variantOffers?.offer;
  const selectedVariantName = selectedVariant?.variantName;

  // Get modal title and content from text field
  const { modalTitle, modalContent } = parseModalContent(selectedVariant?.text || '');
  // Get text before modal content for product details
  const productDetailsText = getTextBeforeModalContent(selectedVariant?.text || '');

  const hasOfferItems = selectedOffer?.offerItems && selectedOffer.offerItems.length > 0;
  const hasProductVariants = selectedOffer?.offerItems?.some(
    item => (item as EnhancedOfferItem).productVariant?.productOptions
  );
  const isEnhancedSystem = hasOfferItems && hasProductVariants;

  useEffect(() => {
    offers.forEach(offer => trackViewedProduct(offer?.salesPageOffer?.offer));
  }, [offers]);

  useEffect(() => {
    if (isEnhancedSystem && selectedOffer?.offerItems) {
      // Parse available options from both productVariant.productOptions AND option1/2/3 columns
      const options = parseProductOptions(selectedOffer.offerItems);
      setAvailableOptions(options);

      const initialVariants: Record<string, SelectedVariantData> = {};
      selectedOffer.offerItems.forEach(item => {
        const enhancedItem = item as EnhancedOfferItem;
        const productVariant = enhancedItem.productVariant;
        const quantity = enhancedItem.storeProductQuantity || 1;

        if (productVariant) {
          // Get options for this specific item
          const itemOptions = parseProductOptionsForItem(enhancedItem);

          // Set default selections for each row
          for (let rowIndex = 0; rowIndex < quantity; rowIndex++) {
            const rowKey = `${item.id}-${rowIndex}`;
            let allVariantOptions: Record<string, any> = {};

            if (productVariant.productOptions) {
              try {
                const productOptions = productVariant.productOptions;
                const parsedOptions =
                  typeof productOptions === 'string' ? JSON.parse(productOptions) : productOptions;
                allVariantOptions = { ...allVariantOptions, ...parsedOptions };
              } catch (e) {
                console.warn(
                  'Failed to parse first variant options:',
                  productVariant.productOptions
                );
              }
            }

            const option1 = (productVariant as any).option1;
            const option2 = (productVariant as any).option2;
            const option3 = (productVariant as any).option3;

            if (option1) allVariantOptions.option1 = option1;
            if (option2) allVariantOptions.option2 = option2;
            if (option3) allVariantOptions.option3 = option3;

            // Set default selections for each available option
            const selectedOptions: Record<string, string> = {};
            itemOptions
              .filter(option => !option.key.startsWith('placeholder_'))
              .forEach(option => {
                // Use the first available value as default
                const firstValue = option.values.find(value => value !== 'N/A');
                if (firstValue) {
                  selectedOptions[option.key] = firstValue;
                }
              });

            initialVariants[rowKey] = {
              variantId: String(productVariant.productVariantId || ''),
              selectedOptions,
            };
          }
        }
      });

      setSelectedVariants(initialVariants);
      setShowVariantSelector(true);
    }
  }, [selectedOffer, isEnhancedSystem]);

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  const buttonBorderColorOverride = buttonBorderColor || buttonBackgroundColor;
  const buttonHoverBorderColorOverride = buttonHoverBorderColor || buttonHoverColor;

  const cssVarsText = `
    .root {
      ${buildCssVar('--heading-color', headingColor, 'var(--primary-color)')}
      ${buildCssVar('--heading-background-color', headingBackgroundColor, 'var(--secondary-color)')}
      ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
      ${buildCssVar('--border-color', borderColor, 'var(--primary-color)')}
      ${buildCssVar('--text-color', textColor, 'var(--text-color)')}
      ${buildCssVar('--compare-at-color', compareAtColor, 'var(--color-negative)')}
      ${buildCssVar('--button-text-color', buttonTextColor, 'var(--text-color-light)')}
      ${buildCssVar('--button-background-color', buttonBackgroundColor, 'var(--primary-color)')}
      ${buildCssVar('--button-hover-color', buttonHoverColor, 'var(--primary-color)')}
      ${buildCssVar('--button-hover-text-color', buttonHoverTextColor, 'var(--text-color-light)')}
      ${buildCssVar('--button-border-color', buttonBorderColorOverride, 'var(--primary-color)')}
      ${buildCssVar('--saving-at-color', undefined, 'var(--color-positive)')}
      ${buildCssVar(
        '--button-hover-border-color',
        buttonHoverBorderColorOverride,
        'var(--primary-color)'
      )}
      ${headingFontOverrides ?? ''}
      ${textFontOverrides ?? ''}
    }
  `;

  const ref = useRef<HTMLDivElement>(null);
  const [tracked, setTracked] = useState(false);

  const { offersConfig } = useFunnelData<SalesFunnelData>();

  useEffect(() => {
    if (tracked) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked) {
          const offerConfig = offersConfig.find(_offer => _offer.id === selectedOffer?.hashedId);

          if (offerConfig) {
            rudder.onPromotionViewed(offerConfig, rudder.PAGE_TYPES.SALES_PAGE);
          }

          setTracked(true);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }

      observer.disconnect();
    };
  }, [tracked, offersConfig, selectedOffer]);

  if (isEnhancedSystem) {
    return (
      <>
        <div ref={ref}></div>
        <style jsx>{cssVarsText}</style>

        <div className={`${styles.enhanced_bundle_container} root`}>
          {/* Step 1: Offer Selection with Accordion Options */}
          <div className={styles.offer_selection}>
            {heading && <h3 className={styles.offer_selection_heading}>{heading}</h3>}
            <div className={styles.offer_cards}>
              {offers.map((offer, index) => {
                const isSelected = selectedVariant === offer;
                const offerData = offer.salesPageOffer?.offer;

                // Calculate savings for badge
                const price = convertPrice(offerData?.price || 0, localFxRate);
                const compareAtPrice = offerData?.compareAtPrice
                  ? convertPrice(offerData.compareAtPrice, localFxRate)
                  : null;
                const percentSaved =
                  compareAtPrice && price ? getPercentSaved(compareAtPrice, price) : 0;
                const percentSavedRounded =
                  compareAtPrice && price
                    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
                    : 0;

                // Determine if we should show savings badge
                const shouldShowSavings = offer.savingsBadgeType && percentSaved > 0;

                return (
                  <div key={index} className={styles.offer_card_container}>
                    <div
                      className={`${styles.offer_card} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleOfferSelection(offer)}
                    >
                      <div className={styles.offer_image_container}>
                        {offer.image ? (
                          <Image
                            src={offer.image.src}
                            alt={offer.variantName || offerData?.name || 'Product'}
                            className={styles.offer_image}
                            width={100}
                            height={100}
                          />
                        ) : (
                          <div className={styles.offer_image_placeholder}>
                            <span>📦</span>
                          </div>
                        )}
                        {isSelected && <div className={styles.image_overlay} />}

                        {/* Savings Badge at bottom of image */}
                        {shouldShowSavings && (
                          <div
                            className={styles.savings_badge}
                            style={{
                              backgroundColor: savingsBadgeBackgroundColor || '#000000',
                              color: savingsBadgeTextColor || '#ffffff',
                            }}
                          >
                            <span>
                              {offer.savingsBadgeType?.toLowerCase() === 'percent'
                                ? `Save ${percentSavedRounded}%`
                                : `Save ${formatCurrency(
                                    compareAtPrice! - price,
                                    false,
                                    currencySymbol
                                  )}`}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className={styles.offer_content}>
                        <h4>{offer.variantName || offerData?.name}</h4>

                        {/* Custom Badges Row */}
                        {(offer.customBadgeText1 || offer.customBadgeText2) && (
                          <div className={styles.custom_badges_row}>
                            {offer.customBadgeText1 && (
                              <div
                                className={styles.custom_badge}
                                style={{
                                  backgroundColor: style?.customBadge1BackgroundColor || '#FFF3BC',
                                  color: style?.customBadge1TextColor || '#000000',
                                }}
                              >
                                <span>{offer.customBadgeText1}</span>
                              </div>
                            )}
                            {offer.customBadgeText2 && (
                              <div
                                className={styles.custom_badge}
                                style={{
                                  backgroundColor: style?.customBadge2BackgroundColor || '#DCFCE7',
                                  color: style?.customBadge2TextColor || '#088758',
                                }}
                              >
                                <span>{offer.customBadgeText2}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className={styles.pricing}>
                        <span className={styles.price}>
                          {currencySymbol}
                          {convertPrice(offerData?.price || 0, localFxRate)}
                        </span>
                        {offerData?.compareAtPrice && (
                          <span className={styles.compare_at}>
                            {currencySymbol}
                            {convertPrice(offerData.compareAtPrice, localFxRate)}
                          </span>
                        )}
                      </div>

                      {isSelected && <div className={styles.card_overlay} />}
                    </div>

                    {/* Accordion Product Options - Show only for selected offer */}
                    {isSelected &&
                      showVariantSelector &&
                      selectedOffer?.offerItems &&
                      availableOptions.length > 0 && (
                        <div className={styles.accordion_options}>
                          <h4>Select Options</h4>

                          {selectedOffer.offerItems.map(item => {
                            const quantity = (item as EnhancedOfferItem).storeProductQuantity || 1;
                            const enhancedItem = item as EnhancedOfferItem;

                            // Get options specific to this item's product variant
                            const itemOptions = parseProductOptionsForItem(enhancedItem);

                            return (
                              <div key={item.id} className={styles.product_section}>
                                {/* Combined Product Container */}
                                <div className={styles.unified_product_container}>
                                  {/* Product Header with Name and Quantity */}
                                  <div className={styles.product_header}>
                                    <div className={styles.product_title}>
                                      <span className={styles.product_name}>{item.name}</span>
                                    </div>
                                    <div className={styles.quantity_info}>
                                      <span>Quantity: {quantity}</span>
                                    </div>
                                  </div>

                                  {/* Generate multiple rows based on quantity */}
                                  {Array.from({ length: quantity }, (_, rowIndex) => {
                                    // Calculate max options across all items for this row
                                    const maxOptions = Math.max(
                                      ...selectedOffer.offerItems.map(offerItem => {
                                        const enhancedOfferItem = offerItem as EnhancedOfferItem;
                                        const offerItemOptions =
                                          parseProductOptionsForItem(enhancedOfferItem);
                                        return offerItemOptions.filter(
                                          option => !option.key.startsWith('placeholder_')
                                        ).length;
                                      })
                                    );

                                    return (
                                      <div
                                        key={`${item.id}-${rowIndex}`}
                                        className={`${styles.table_row} ${
                                          maxOptions === 3
                                            ? styles.max_three_options
                                            : styles.max_two_options
                                        }`}
                                      >
                                        <div className={styles.item_number}>{rowIndex + 1}</div>
                                        <div className={styles.item_image}>
                                          {item.image ? (
                                            <Image
                                              src={item.image.src}
                                              alt={item.name || 'Product'}
                                              className={styles.product_img}
                                              width={50}
                                              height={50}
                                            />
                                          ) : (
                                            <div className={styles.placeholder_img}>
                                              <span>📦</span>
                                            </div>
                                          )}
                                        </div>

                                        {/* Option Columns - Only show options available for this specific item */}
                                        {(() => {
                                          const filteredOptions = itemOptions.filter(
                                            option => !option.key.startsWith('placeholder_')
                                          );
                                          const isSingleOption = filteredOptions.length === 1;

                                          return filteredOptions.map(option => (
                                            <div
                                              key={option.key}
                                              className={`${styles.option_column} ${
                                                isSingleOption ? styles.single_option : ''
                                              }`}
                                              data-label={option.label}
                                            >
                                              <select
                                                className={`${styles.option_select} ${
                                                  selectedVariants[`${item.id}-${rowIndex}`]
                                                    ?.selectedOptions?.[option.key]
                                                    ? styles.selected
                                                    : ''
                                                }`}
                                                value={
                                                  selectedVariants[`${item.id}-${rowIndex}`]
                                                    ?.selectedOptions?.[option.key] || ''
                                                }
                                                onChange={e =>
                                                  handleOptionChange(
                                                    `${item.id}-${rowIndex}`,
                                                    option.key,
                                                    e.target.value
                                                  )
                                                }
                                              >
                                                <option value="">Choose {option.label}</option>
                                                {option.values
                                                  .filter(value => value !== 'N/A')
                                                  .map(value => (
                                                    <option key={value} value={value}>
                                                      {value}
                                                    </option>
                                                  ))}
                                              </select>
                                            </div>
                                          ));
                                        })()}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}

                          {/* Product Details Section */}
                          <div className={styles.product_details_section}>
                            <div className={styles.details_header}>
                              <h3>Product Details</h3>
                              {modalTitle && modalContent && (
                                <button
                                  className={styles.modal_trigger_btn}
                                  onClick={() => setShowDetailsModal(true)}
                                >
                                  <span className={styles.info_icon}>ⓘ</span>
                                  {modalTitle}
                                </button>
                              )}
                            </div>
                            <div className={styles.details_content}>
                              <MarkdownText queryParams={null} text={productDetailsText} />
                            </div>
                          </div>

                          {/* Buy Button inside accordion */}
                          <button
                            className={`${styles.accordion_buy_btn} ${
                              allVariantsSelected() ? styles.ready : styles.disabled
                            }`}
                            onClick={handleEnhancedPurchase}
                            disabled={!allVariantsSelected() || shouldShowActivityIndicator}
                          >
                            {shouldShowActivityIndicator
                              ? 'Processing...'
                              : !allVariantsSelected()
                              ? 'Select Options'
                              : `Get ${selectedOffer?.name || 'Bundle'} →`}
                          </button>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Modal for Materials Details */}
        {showDetailsModal && (
          <div className={styles.modal_overlay} onClick={() => setShowDetailsModal(false)}>
            <div className={styles.modal_content} onClick={e => e.stopPropagation()}>
              <div className={styles.modal_header}>
                <button
                  className={styles.modal_close_btn}
                  onClick={() => setShowDetailsModal(false)}
                >
                  ×
                </button>
              </div>
              <div className={styles.modal_body}>
                <MarkdownText queryParams={null} text={modalContent} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // LEGACY SYSTEM: Keep original Mobile/Desktop components for backward compatibility
  return (
    <>
      <div ref={ref}></div>
      <MediaQuery maxWidth={980}>
        <MultiVariantBundleItemMobile
          ctaText={ctaText}
          getCheckoutClickHandler={getCheckoutClickHandler}
          selectedVariantName={selectedVariantName}
          shouldShowActivityIndicator={shouldShowActivityIndicator}
          offers={offers}
          image={selectedVariant?.image}
          cssVarsText={cssVarsText}
          compareAtPrice={convertPrice(selectedOffer?.compareAtPrice || 0, localFxRate)}
          price={convertPrice(selectedOffer?.price || 0, localFxRate)}
          description={selectedVariant?.text}
          heading={heading}
          offerId={selectedOffer?.hashedId}
          offerName={selectedOffer?.name}
          onVariantChange={handleVariantChange}
          variantName={selectedVariant?.name}
          rating={selectedVariant?.rating}
          starColor={starColor}
          starEmptyColor={starEmptyColor}
          currencySymbol={currencySymbol}
        />
      </MediaQuery>
      <MediaQuery minWidth={981}>
        <MultiVariantBundleItemDesktop
          ctaText={ctaText}
          getCheckoutClickHandler={getCheckoutClickHandler}
          selectedVariantName={selectedVariantName}
          shouldShowActivityIndicator={shouldShowActivityIndicator}
          image={selectedVariant?.image}
          cssVarsText={cssVarsText}
          compareAtPrice={convertPrice(selectedOffer?.compareAtPrice || 0, localFxRate)}
          price={convertPrice(selectedOffer?.price || 0, localFxRate)}
          description={selectedVariant?.text}
          heading={heading}
          offers={offers}
          offerId={selectedOffer?.hashedId}
          offerName={selectedOffer?.name}
          onVariantChange={handleVariantChange}
          variantName={selectedVariant?.name}
          currencySymbol={currencySymbol}
        />
      </MediaQuery>
    </>
  );

  function parseModalContent(text: string): { modalTitle: string; modalContent: string } {
    if (!text) return { modalTitle: '', modalContent: '' };

    // Look for [ModalTitle] pattern
    const modalTitleMatch = text.match(/\[ModalTitle\]\s*:\s*([^\n\r]+)/i);
    if (!modalTitleMatch) {
      return { modalTitle: '', modalContent: '' };
    }

    const parsedModalTitle = modalTitleMatch[1].trim();

    // Look for [ModalContent] pattern and extract everything after the colon
    const modalContentMatch = text.match(/\[ModalContent\]\s*:\s*([\s\S]*?)(?=\[|$)/i);
    if (!modalContentMatch) {
      return { modalTitle: parsedModalTitle, modalContent: '' };
    }

    // Get the raw content after [ModalContent] : (including ## Ingredients header)
    const parsedModalContent = modalContentMatch[1].trim();
    return { modalTitle: parsedModalTitle, modalContent: parsedModalContent };
  }

  function getTextBeforeModalContent(text: string): string {
    if (!text) return '';

    // Look for [ModalContent] pattern and get everything before it
    const modalContentMatch = text.match(/\[ModalContent\]\s*:/i);
    if (!modalContentMatch) {
      return text; // If no ModalContent section, return all text
    }

    // Get everything before [ModalContent]
    const modalContentIndex = modalContentMatch.index!;
    const result = text.substring(0, modalContentIndex).trim();

    // Remove [ModalTitle] if it exists
    const cleanResult = result.replace(/\[ModalTitle\]\s*:\s*[^\n\r]+/gi, '').trim();
    return cleanResult;
  }

  function parseProductOptions(offerItems: OfferItems[]): ProductOption[] {
    const optionKeysMap = new Map<string, Set<string>>();

    offerItems.forEach(item => {
      const enhancedItem = item as EnhancedOfferItem;
      const productVariant = enhancedItem.productVariant;

      if (productVariant) {
        const productOptions = productVariant.productOptions;
        if (productOptions) {
          let parsedOptions: Record<string, any> = {};

          try {
            parsedOptions =
              typeof productOptions === 'string' ? JSON.parse(productOptions) : productOptions;
          } catch (e) {
            console.warn('Failed to parse product_options JSON:', productOptions);
          }

          // Process each option from product_options
          Object.entries(parsedOptions).forEach(([key, value]) => {
            if (value && (typeof value === 'string' || typeof value === 'number')) {
              const stringValue = String(value).trim();
              if (stringValue) {
                if (!optionKeysMap.has(key)) {
                  optionKeysMap.set(key, new Set());
                }

                optionKeysMap.get(key)!.add(stringValue);
              }
            }
          });
        }

        const option1 = (productVariant as any).option1;
        const option2 = (productVariant as any).option2;
        const option3 = (productVariant as any).option3;

        if (option1) {
          const stringValue = String(option1).trim();
          if (stringValue) {
            if (!optionKeysMap.has('option1')) {
              optionKeysMap.set('option1', new Set());
            }

            optionKeysMap.get('option1')!.add(stringValue);
          }
        }

        if (option2) {
          const stringValue = String(option2).trim();
          if (stringValue) {
            if (!optionKeysMap.has('option2')) {
              optionKeysMap.set('option2', new Set());
            }

            optionKeysMap.get('option2')!.add(stringValue);
          }
        }

        if (option3) {
          const stringValue = String(option3).trim();
          if (stringValue) {
            if (!optionKeysMap.has('option3')) {
              optionKeysMap.set('option3', new Set());
            }

            optionKeysMap.get('option3')!.add(stringValue);
          }
        }
      }
    });

    // Convert to ProductOption array with fully dynamic labels
    const getOptionLabel = (key: string): string => {
      // dynamic transformation
      return key
        .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to spaces
        .replace(/[_-]/g, ' ') // underscores and hyphens to spaces
        .replace(/\b\w/g, l => l.toUpperCase()) // capitalize first letter of each word
        .trim();
    };

    const allOptions = Array.from(optionKeysMap.entries()).map(([key, valueSet]) => ({
      key,
      label: getOptionLabel(key),
      values: Array.from(valueSet).sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }),
    }));

    const limitedOptions = allOptions.slice(0, 3);

    while (limitedOptions.length < 3) {
      limitedOptions.push({
        key: `placeholder_${limitedOptions.length}`,
        label: `Option ${limitedOptions.length + 1}`,
        values: ['N/A'],
      });
    }

    return limitedOptions;
  }

  function findMatchingVariant(offerItemId: string, selectedOptions: Record<string, string>) {
    const offerItem = selectedOffer?.offerItems?.find(item => String(item.id) === offerItemId);
    const enhancedItem = offerItem as EnhancedOfferItem;

    if (!enhancedItem?.productVariant) return null;

    const productVariant = enhancedItem.productVariant;
    let variantOptions: Record<string, any> = {};

    if (productVariant.productOptions) {
      try {
        const productOptions = productVariant.productOptions;
        const parsedOptions =
          typeof productOptions === 'string' ? JSON.parse(productOptions) : productOptions;
        variantOptions = { ...variantOptions, ...parsedOptions };
      } catch (e) {
        console.warn('Failed to parse variant options:', productVariant.productOptions);
      }
    }

    const option1 = (productVariant as any).option1;
    const option2 = (productVariant as any).option2;
    const option3 = (productVariant as any).option3;

    if (option1) variantOptions.option1 = option1;
    if (option2) variantOptions.option2 = option2;
    if (option3) variantOptions.option3 = option3;

    // Check if all selected options match this variant
    const matches = Object.entries(selectedOptions).every(([key, value]) => {
      const variantValue = variantOptions[key];
      return variantValue && String(variantValue).trim() === value;
    });

    return matches ? productVariant : null;
  }

  function handleVariantChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const variant = offers.find(offer => offer.variantName === e.target.value);
    if (!variant) return;

    const offerConfig = offersConfig.find(
      _offer => _offer.id === variant?.salesPageOffer.offer?.hashedId
    );

    if (offerConfig) {
      rudder.onPromotionClicked(offerConfig, rudder.PAGE_TYPES.SALES_PAGE);
    }

    setSelectedVariant(variant);
  }

  function handleOfferSelection(offer: MvbSalesOffer) {
    setSelectedVariant(offer);

    const offerConfig = offersConfig.find(
      _offer => _offer.id === offer.salesPageOffer.offer?.hashedId
    );
    if (offerConfig) {
      rudder.onPromotionClicked(offerConfig, rudder.PAGE_TYPES.SALES_PAGE);
    }
  }

  function handleOptionChange(offerItemId: string, optionKey: string, value: string) {
    setSelectedVariants(prev => {
      const updatedOptions = {
        ...prev[offerItemId]?.selectedOptions,
        [optionKey]: value,
      };

      // Find the matching variant for the updated options
      const matchingVariant = findMatchingVariant(offerItemId, updatedOptions);

      return {
        ...prev,
        [offerItemId]: {
          variantId: matchingVariant ? String(matchingVariant.productVariantId) : '',
          selectedOptions: updatedOptions,
        },
      };
    });
  }

  function allVariantsSelected(): boolean {
    if (!selectedOffer?.offerItems) return true;

    return selectedOffer.offerItems.every(item => {
      const enhancedItem = item as EnhancedOfferItem;
      if (!enhancedItem.productVariant?.productOptions) return true;

      const quantity = enhancedItem.storeProductQuantity || 1;

      // Get options specific to this item
      const itemOptions = parseProductOptionsForItem(enhancedItem);

      // Check each row for this item
      for (let rowIndex = 0; rowIndex < quantity; rowIndex++) {
        const rowKey = `${item.id}-${rowIndex}`;
        const selectedVariantData = selectedVariants[rowKey];

        if (!selectedVariantData) return false;

        const hasAllOptions = itemOptions
          .filter(option => !option.key.startsWith('placeholder_'))
          .every(option => {
            const selectedValue = selectedVariantData.selectedOptions?.[option.key];
            return selectedValue && selectedValue.trim() !== '' && selectedValue !== 'N/A';
          });

        if (!hasAllOptions) return false;
      }

      return true;
    });
  }

  function handleEnhancedPurchase(e?: React.MouseEvent) {
    if (selectedOffer?.hashedId) {
      const cartItems = [
        {
          offerId: selectedOffer.hashedId,
          quantity: 1,
        },
      ];

      const checkoutHandler = getCheckoutClickHandler(cartItems);

      const mockEvent =
        e ||
        ({
          preventDefault: () => {},
          stopPropagation: () => {},
          target: {},
          currentTarget: {},
        } as React.MouseEvent);

      checkoutHandler(mockEvent);
    }
  }
}

// Parse options for a single item
function parseProductOptionsForItem(item: EnhancedOfferItem): ProductOption[] {
  const optionKeysMap = new Map<string, Set<string>>();
  const productVariant = item.productVariant;

  if (productVariant) {
    const productOptions = productVariant.productOptions;
    if (productOptions) {
      let parsedOptions: Record<string, any> = {};

      try {
        parsedOptions =
          typeof productOptions === 'string' ? JSON.parse(productOptions) : productOptions;
      } catch (e) {
        console.warn('Failed to parse product_options JSON:', productOptions);
      }

      // Process each option from product_options
      Object.entries(parsedOptions).forEach(([key, value]) => {
        if (value && (typeof value === 'string' || typeof value === 'number')) {
          const stringValue = String(value).trim();
          if (stringValue) {
            if (!optionKeysMap.has(key)) {
              optionKeysMap.set(key, new Set());
            }

            optionKeysMap.get(key)!.add(stringValue);
          }
        }
      });
    }

    const option1 = (productVariant as any).option1;
    const option2 = (productVariant as any).option2;
    const option3 = (productVariant as any).option3;

    if (option1) {
      const stringValue = String(option1).trim();
      if (stringValue) {
        if (!optionKeysMap.has('option1')) {
          optionKeysMap.set('option1', new Set());
        }

        optionKeysMap.get('option1')!.add(stringValue);
      }
    }

    if (option2) {
      const stringValue = String(option2).trim();
      if (stringValue) {
        if (!optionKeysMap.has('option2')) {
          optionKeysMap.set('option2', new Set());
        }

        optionKeysMap.get('option2')!.add(stringValue);
      }
    }

    if (option3) {
      const stringValue = String(option3).trim();
      if (stringValue) {
        if (!optionKeysMap.has('option3')) {
          optionKeysMap.set('option3', new Set());
        }

        optionKeysMap.get('option3')!.add(stringValue);
      }
    }
  }

  // Convert to ProductOption array with fully dynamic labels
  const getOptionLabel = (key: string): string => {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  };

  const allOptions = Array.from(optionKeysMap.entries()).map(([key, valueSet]) => ({
    key,
    label: getOptionLabel(key),
    values: Array.from(valueSet).sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    }),
  }));

  const limitedOptions = allOptions.slice(0, 3);

  while (limitedOptions.length < 3) {
    limitedOptions.push({
      key: `placeholder_${limitedOptions.length}`,
      label: `Option ${limitedOptions.length + 1}`,
      values: ['N/A'],
    });
  }

  return limitedOptions;
}
